import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useSearchParams, useNavigate } from "react-router-dom";
import { useActiveContext } from "@/context/AdaContext";
import AdaBubble from "@/components/ada/AdaBubble";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import FenceWatermark from "@/components/Watermark";
import PdfCanvasViewer from "@/components/PdfCanvasViewer";
import { toast } from "@/components/Toaster";
import { ArrowLeft, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, StickyNote, X, Shield, ShieldCheck, KeyRound, ZoomIn, ZoomOut, RotateCcw, Download, Lock, FileType2 } from "lucide-react";
import * as mammoth from "mammoth";
import * as XLSX from "xlsx";
import DOMPurify from "dompurify";
import RequestAccessModal from "@/components/RequestAccessModal";
import { useFileProgress } from "@/context/FileProgressContext";

// Sanitiser config shared by both DOCX (mammoth) and XLSX (sheetjs) renders.
// Both libraries produce HTML from user-uploaded files, so we strip every
// active element / attribute before letting React inject it into the DOM.
// FORBID_TAGS removes script/object/embed/iframe/etc.; the default DOMPurify
// allow-list keeps the formatting tags we need (table, p, h1-h6, strong, etc.).
const SANITIZE_CFG = {
  FORBID_TAGS: ["script", "object", "embed", "iframe", "form", "input", "meta", "link"],
  FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "onfocus", "onblur",
                "onsubmit", "onchange", "formaction", "srcdoc"],
};
const sanitize = (html) => DOMPurify.sanitize(html || "", SANITIZE_CFG);

const isPdf = (d) => (d?.mime || "").includes("pdf") || /\.pdf$/i.test(d?.name || "");
const isImage = (d) => (d?.mime || "").startsWith("image/") || /\.(png|jpe?g|gif|webp|svg)$/i.test(d?.name || "");
const isDocx = (d) => /\.docx$/i.test(d?.name || "") || (d?.mime || "").includes("wordprocessingml");
const isXlsx = (d) => /\.xlsx?$/i.test(d?.name || "") || (d?.mime || "").includes("spreadsheetml");
const isPptx = (d) => /\.pptx?$/i.test(d?.name || "") || (d?.mime || "").includes("presentationml");
const isText = (d) => (d?.mime || "").startsWith("text/") || /\.(txt|md|csv|json)$/i.test(d?.name || "");

/**
 * Coerce near-white inline text colors in SheetJS's emitted HTML to a
 * legible dark grey, so spreadsheets authored with light-on-white themes
 * (Excel's "ION", "Wisp" or any custom near-white text style) stay
 * readable inside our viewer canvas.
 *
 * We intentionally only touch the `color:` declaration — `background-color`
 * and other fill rules are left alone so conditional highlights (yellow,
 * red, etc.) still come through. Threshold is generous (luminance > 0.82)
 * to catch off-white greys without affecting deliberately mid/dark colours.
 */
function normalizeXlsxLegibility(html) {
  if (!html || typeof html !== "string") return html;
  // Parse "#fff", "#ffffff", "#FFFFFF", or "rgb(255, 255, 255)" -> luminance.
  const parseColor = (c) => {
    c = c.trim().toLowerCase();
    if (c.startsWith("#")) {
      let hex = c.slice(1);
      if (hex.length === 3) hex = hex.split("").map((x) => x + x).join("");
      if (hex.length !== 6) return null;
      const n = parseInt(hex, 16);
      return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    }
    const m = c.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
    if (m) return [+m[1], +m[2], +m[3]];
    return null;
  };
  const luminance = ([r, g, b]) => (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  // Rewrite every `color: <value>` inline declaration whose luminance is
  // above the threshold. Anchored inside `style="…"` so we don't mistakenly
  // touch text outside of HTML attributes.
  return html.replace(
    /(style\s*=\s*"[^"]*?)color\s*:\s*([^;"]+)/gi,
    (full, prefix, colorVal) => {
      const rgb = parseColor(colorVal);
      if (!rgb) return full;
      if (luminance(rgb) > 0.82) {
        return `${prefix}color:#1a1a1a`;
      }
      return full;
    },
  );
}

// Walk up from `start` and find the first ancestor whose vertical
// overflow allows scrolling (so toolbar arrows can drive the same scroll
// container the user already scrolls with their wheel/trackpad).
function findScrollable(start) {
  let el = start;
  while (el && el !== document.body) {
    const cs = window.getComputedStyle(el);
    if (/auto|scroll|overlay/.test(cs.overflowY) && el.scrollHeight > el.clientHeight) {
      return el;
    }
    el = el.parentElement;
  }
  return document.scrollingElement || document.documentElement;
}

// Scroll the nearest scrollable ancestor of `start` by `fraction` of its
// own client height — used by DOCX page-up / page-down arrows.
function scrollDocxBy(start, fraction) {
  if (!start) return;
  const el = findScrollable(start);
  if (!el) return;
  el.scrollBy({ top: el.clientHeight * fraction, behavior: "smooth" });
}

export default function DocumentViewer() {
  const { roomId, docId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const initialPage = parseInt(searchParams.get("page") || "1", 10) || 1;

  // Close handler — prefer history-back so the user returns to the exact
  // folder + scroll state they came from. Falls back to the room root when
  // there is no SPA history entry (direct URL or notification deep-link).
  const closeDoc = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate(`/app/rooms/${roomId}`);
  };
  const [doc, setDoc] = useState(null);
  const [page, setPage] = useState(initialPage);
  const [perms, setPerms] = useState(null);
  const [room, setRoom] = useState(null);
  const [imageBlobUrl, setImageBlobUrl] = useState(null);
  const [pdfData, setPdfData] = useState(null);
  const [pdfPages, setPdfPages] = useState(0);
  const [renderedHtml, setRenderedHtml] = useState("");
  const [textContent, setTextContent] = useState("");
  const [sheetTab, setSheetTab] = useState(0);
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [noteBusy, setNoteBusy] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const fileProgress = useFileProgress();
  // Reader zoom level — applied to PDFs via per-page canvas re-render
  // and to DOCX/image surfaces via CSS transform. Clamped to a sensible
  // range; the toolbar buttons step in 10% increments and "Reset" snaps
  // back to 1× (fit-to-width for PDFs, native size for DOCX).
  const ZOOM_MIN = 0.5;
  const ZOOM_MAX = 3.0;
  const ZOOM_STEP = 0.1;
  const [zoom, setZoom] = useState(1);
  const zoomIn = () => setZoom((z) => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)));
  const zoomOut = () => setZoom((z) => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)));
  const zoomReset = () => setZoom(1);

  // Publish current focus to Ada so she can answer "summarise this",
  // "what's on page 4", etc. against THIS document. Cleared on unmount.
  useActiveContext(
    doc?.id
      ? { type: "document", doc_id: doc.id, name: doc.name, page }
      : null,
  );

  // Admin flag for the persistent Ada bubble mount on this page.
  const viewerIsAdmin = user?.role === "admin";

  // Keyboard zoom shortcuts — Ctrl/Cmd + / − / 0. We attach a single
  // document-level listener instead of relying on focus inside the
  // viewer, because the user might be scrolling with the mouse over
  // the canvas (which never has DOM focus). The handler intentionally
  // intercepts BOTH `=` and `+` for zoom-in (US keyboards send `=`
  // for unshifted `+`) and the numeric-row `0` for reset. We let
  // the browser keep ownership when the user is typing in an input.
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      if (e.key === "+" || e.key === "=") { e.preventDefault(); zoomIn(); }
      else if (e.key === "-" || e.key === "_") { e.preventDefault(); zoomOut(); }
      else if (e.key === "0") { e.preventDefault(); zoomReset(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  // Refs used to drive imperative scrolling from the toolbar arrows.
  const pdfRef = useRef(null);     // PdfCanvasViewer imperative handle
  const docxRef = useRef(null);    // DOCX body wrapper, for page-up/down

  useEffect(() => {
    (async () => {
      setLoading(true);
      // Bottom-of-screen progress bar — appears the instant the user lands
      // on the viewer route. We can't know the file name yet, so the label
      // starts generic and is upgraded once the document metadata resolves.
      const progressId = fileProgress.start({ label: "Opening document…" });
      try {
        const [r, perm, list] = await Promise.all([
          api.get(`/rooms/${roomId}`),
          api.get(`/rooms/${roomId}/documents/${docId}/permissions`),
          api.get(`/rooms/${roomId}/documents`),
        ]);
        setRoom(r.data); setPerms(perm.data);
        const d = list.data.find((x) => x.id === docId);
        setDoc(d);
        if (!perm.data.view || !d) {
          fileProgress.complete(progressId);
          setLoading(false);
          return;
        }
        fileProgress.setLabel(progressId, `Opening — ${d.name}`);

        const res = await api.get(`/rooms/${roomId}/documents/${docId}/view`, {
          params: { page: 1 },
          responseType: "blob",
          onDownloadProgress: (evt) => {
            fileProgress.update(progressId, {
              loaded: evt.loaded,
              total: evt.total,
            });
          },
        });

        // The backend may serve a watermarked flat PDF for users who
        // lack `original` access (SEC-002 hardening) — even when the
        // doc metadata says .docx. Trust the response content-type
        // over the doc's declared mime so the right renderer is used.
        const ctype = (res.headers?.["content-type"] || res.data?.type || "").toLowerCase();
        const servedAsPdf = ctype.includes("application/pdf");

        if (isPdf(d) || isPptx(d) || servedAsPdf) {
          // Render via pdfjs canvas — never expose a blob URL the user can save.
          // PPTX is server-side converted to PDF and streamed here as
          // application/pdf so the same canvas viewer handles both.
          const buf = await res.data.arrayBuffer();
          setPdfData(buf);
        } else if (isDocx(d)) {
          const buf = await res.data.arrayBuffer();
          try {
            const r2 = await mammoth.convertToHtml({ arrayBuffer: buf });
            setRenderedHtml(r2.value || "");
          } catch (parseErr) {
            console.error("docx render failed", parseErr);
            setRenderedHtml("");
          }
        } else if (isXlsx(d)) {
          const buf = await res.data.arrayBuffer();
          try {
            const wb = XLSX.read(buf, { type: "array" });
            const parsed = (wb.SheetNames || []).map((name) => ({
              name,
              html: normalizeXlsxLegibility(
                XLSX.utils.sheet_to_html(wb.Sheets[name], { editable: false })
              ),
            }));
            setSheets(parsed);
          } catch (parseErr) {
            console.error("xlsx render failed", parseErr);
            setSheets([]);
          }
        } else if (isText(d)) {
          setTextContent(await res.data.text());
        } else if (isImage(d)) {
          setImageBlobUrl(URL.createObjectURL(res.data));
        }
      } catch (e) {
        const status = e?.response?.status;
        if (status === 403) setErr("Access restricted — you do not have permission to view this document.");
        else if (status === 404) setErr("Document not found.");
        else setErr(e?.response?.data?.detail || "Could not load document.");
        fileProgress.error(progressId, "Could not open document");
      } finally {
        setLoading(false);
        fileProgress.complete(progressId);
      }
    })();
    return () => { if (imageBlobUrl) URL.revokeObjectURL(imageBlobUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, docId]);

  useEffect(() => {
    if (doc && perms?.view) {
      api.post(`/rooms/${roomId}/documents/${docId}/track`, { action: "page_view", page });
    }
  }, [page, doc, perms, roomId, docId]);

  // Engagement tracking: heartbeat every 20s while the viewer is mounted +
  // a closing pulse on unmount. Pauses while the tab is hidden so we don't
  // over-count time the user isn't actually reading.
  useEffect(() => {
    if (!doc || !perms?.view) return;
    let cancelled = false;
    let visible = !document.hidden;
    const ping = async () => {
      if (cancelled || !visible) return;
      try { await api.post(`/rooms/${roomId}/documents/${docId}/view-heartbeat`); }
      catch { /* ignore — engagement is best-effort */ }
    };
    // Initial heartbeat ASAP so a short visit still gets logged.
    ping();
    const id = setInterval(ping, 20000);
    const onVis = () => {
      visible = !document.hidden;
      if (visible) ping();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
      // Closing pulse — fire-and-forget, ignore errors (e.g. unmount race).
      api.post(`/rooms/${roomId}/documents/${docId}/view-end`).catch(() => {});
    };
  }, [doc, perms, roomId, docId]);

  // Block save/print keyboard shortcuts whenever the viewer is mounted —
  // but only for non-admins. Admins are the room owners and should be
  // able to print/save the original document like any normal file.
  const isAdmin = user?.role === "admin";
  useEffect(() => {
    if (isAdmin) return undefined;
    const blockSavePrint = (e) => {
      const k = (e.key || "").toLowerCase();
      if ((e.ctrlKey || e.metaKey) && (k === "s" || k === "p")) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener("keydown", blockSavePrint, true);
    return () => document.removeEventListener("keydown", blockSavePrint, true);
  }, [isAdmin]);

  // Contextual download availability, computed once from the doc perms
  // returned by /permissions AND the room-level allow_downloads toggle
  // (which the backend enforces on every fmt). Admins/Owners see only
  // the ORIGINAL icon — they're already viewing the raw source (per the
  // "Admin view" pill), so an encrypted-PDF or flat-PDF download would
  // be redundant. Non-admins see whichever icons match their per-doc
  // permission grant, so this mirrors DocumentsTab.permsFor().
  const dlPerms = (() => {
    if (isAdmin) return { original: true, encrypted: false, pdf: false };
    if (!perms) return { original: false, encrypted: false, pdf: false };
    const gate = room?.allow_downloads !== false;
    return {
      original: !!(perms.original && gate),
      encrypted: !!(perms.encrypted && gate),
      pdf: !!(perms.pdf && gate),
    };
  })();
  const canDownloadAny = dlPerms.original || dlPerms.encrypted || dlPerms.pdf;

  // Admin/Owner-only: download the ORIGINAL file directly from the
  // viewer toolbar. Mirrors DocumentsTab.download() — same endpoint,
  // same progress bar wiring, same file naming. Non-admins never see
  // the button, but the /download endpoint additionally enforces
  // permissions on the server side, so this cannot leak the raw file
  // even if the pill were forced into the DOM.
  const [downloading, setDownloading] = useState(null); // holds active fmt or null
  const downloadAs = async (fmt) => {
    if (!doc || downloading) return;
    setDownloading(fmt);
    const fmtLabel =
      fmt === "encrypted" ? "Encrypted PDF"
      : fmt === "pdf"     ? "Flat PDF"
      : "Original";
    const taskId = fileProgress.start({
      label: `Preparing ${fmtLabel} — ${doc.name}`,
    });
    try {
      const res = await api.get(
        `/rooms/${roomId}/documents/${docId}/download`,
        {
          params: { fmt },
          responseType: "blob",
          onDownloadProgress: (evt) => {
            fileProgress.update(taskId, {
              loaded: evt.loaded,
              total: evt.total || evt.event?.target?.getResponseHeader?.("content-length"),
            });
          },
        }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const base = doc.name.replace(/\.[^.]+$/, "");
      a.download =
        fmt === "encrypted" ? `${base}.encrypted.pdf`
        : fmt === "pdf"     ? `${base}.pdf`
        : doc.name;
      a.click();
      URL.revokeObjectURL(url);
      fileProgress.setLabel(taskId, `Ready — ${a.download}`);
      fileProgress.complete(taskId);
    } catch (e) {
      fileProgress.error(taskId, "Download failed");
      toast.error(`Could not download ${fmtLabel.toLowerCase()}`);
    } finally {
      setDownloading(null);
    }
  };

  // Honor a `?page=N` deep-link once the PDF has finished loading. We
  // hold the requested page in a ref so it only fires once per mount.
  const pendingDeepLinkRef = useRef(initialPage > 1 ? initialPage : null);
  useEffect(() => {
    if (!pdfPages || !pendingDeepLinkRef.current) return;
    const target = Math.min(pdfPages, pendingDeepLinkRef.current);
    pendingDeepLinkRef.current = null;
    setTimeout(() => pdfRef.current?.scrollToPage?.(target), 50);
  }, [pdfPages]);

  if (loading) return <div className="p-8 label-mono text-muted-foreground">— Loading document —</div>;
  if (!doc) return <div className="p-8 label-mono text-muted-foreground">— Document not found —</div>;
  if (perms && !perms.view) {
    return (
      <div className="p-8">
        <Link to={`/app/rooms/${roomId}`} className="label-mono text-muted-foreground hover:text-foreground inline-flex items-center gap-2">
          <ArrowLeft className="w-3 h-3" /> Back
        </Link>
        <div className="mt-8 border border-destructive/30 bg-destructive/10 text-destructive p-6 rounded-md">
          <div className="font-medium">You do not have view permission for this document.</div>
          <p className="text-sm opacity-90 mt-2">
            Ask an App Owner for scoped access — the request will be pre-filled
            with this exact document so the Owner can grant the right perms in one click.
          </p>
          <button
            type="button"
            onClick={() => setRequestOpen(true)}
            data-testid="request-access-from-viewer-denied"
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition"
          >
            <KeyRound className="w-3.5 h-3.5" /> Request access to this document
          </button>
        </div>
        {requestOpen && (
          <RequestAccessModal
            roomId={roomId}
            docId={docId}
            docName={doc?.name}
            isFolder={doc?.type === "folder"}
            onClose={() => setRequestOpen(false)}
            onDone={() => setRequestOpen(false)}
          />
        )}
      </div>
    );
  }

  const totalPages = pdfPages || doc.pages || 1;

  return (
    <div className="relative">
      {/* Sticky viewer toolbar — kept under the notifications popover
          (z-30 here, popover is z-[100]) so opening notifications never
          gets hidden by it. */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur border-b border-border px-6 py-3 flex items-center justify-between">
        <button
          type="button"
          onClick={closeDoc}
          className="label-mono text-muted-foreground hover:text-foreground inline-flex items-center gap-2"
        >
          <ArrowLeft className="w-3 h-3" /> Back to folder
        </button>
        <div className="flex items-center gap-3">
          {isAdmin ? (
            <>
              <span
                data-testid="admin-original-pill"
                className="hidden sm:inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-primary/30 bg-primary/10 text-primary mono text-[10px] uppercase tracking-wider"
                title="You're viewing the raw source. Watermarks and fence restrictions are off for room admins."
              >
                <ShieldCheck className="w-3 h-3" />
                Original · Admin view
              </span>
            </>
          ) : (
            <>
              <span
                data-testid="fence-active-pill"
                className="hidden sm:inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-amber-500/30 bg-amber-500/10 text-amber-500 mono text-[10px] uppercase tracking-wider"
                title="Watermarked, copy-blocked, save/print-blocked. Every view is logged for provenance."
              >
                <Shield className="w-3 h-3" />
                Fence-view active
              </span>
              {perms && !Object.values(perms).every(Boolean) && (
                <button
                  type="button"
                  onClick={() => setRequestOpen(true)}
                  data-testid="request-access-from-viewer"
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-500 hover:bg-emerald-500/10 mono text-[10px] uppercase tracking-wider transition"
                  title="Ask an App Owner for more permissions on this document"
                >
                  <KeyRound className="w-3 h-3" />
                  <span className="hidden md:inline">Need more access?</span>
                  <span className="md:hidden">Access</span>
                </button>
              )}
            </>
          )}
          {/* Contextual download icon strip — visible to every viewer,
              but each icon only renders if the current user has the
              matching permission (admins get all three, per dlPerms).
              Order matches the row icons and right-click menu: Original,
              Encrypted PDF, Flat PDF. Icon-only to keep the toolbar
              compact; tooltips carry the label. */}
          {canDownloadAny && (
            <div
              className="flex items-center gap-1 border border-border rounded-md min-h-[34px]"
              data-testid="viewer-download-strip"
            >
              {dlPerms.original && (
                <button
                  type="button"
                  onClick={() => downloadAs("original")}
                  disabled={downloading === "original"}
                  data-testid="viewer-download-original-btn"
                  className="p-2 hover:bg-secondary transition disabled:opacity-50 disabled:cursor-not-allowed text-primary"
                  aria-label="Download original"
                  title={isAdmin ? "Download original (admin)" : "Download original"}
                >
                  <Download className="w-3.5 h-3.5" />
                </button>
              )}
              {dlPerms.encrypted && (
                <button
                  type="button"
                  onClick={() => downloadAs("encrypted")}
                  disabled={downloading === "encrypted"}
                  data-testid="viewer-download-encrypted-btn"
                  className="p-2 hover:bg-secondary transition disabled:opacity-50 disabled:cursor-not-allowed text-amber-500"
                  aria-label="Download encrypted PDF"
                  title="Download encrypted PDF (watermarked, view-only, copy-blocked)"
                >
                  <Lock className="w-3.5 h-3.5" />
                </button>
              )}
              {dlPerms.pdf && (
                <button
                  type="button"
                  onClick={() => downloadAs("pdf")}
                  disabled={downloading === "pdf"}
                  data-testid="viewer-download-pdf-btn"
                  className="p-2 hover:bg-secondary transition disabled:opacity-50 disabled:cursor-not-allowed text-sky-500"
                  aria-label="Download PDF"
                  title="Download flat PDF (non-editable)"
                >
                  <FileType2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}
          {(isPdf(doc) || isPptx(doc) || isDocx(doc) || isImage(doc) || isXlsx(doc)) && (
            <div
              className="flex items-center gap-1 border border-border rounded-md"
              data-testid="zoom-controls"
              title="Zoom in / out / reset · keyboard: Ctrl/Cmd + / − / 0"
            >
              <button
                data-testid="zoom-out-btn"
                onClick={zoomOut}
                disabled={zoom <= ZOOM_MIN + 1e-3}
                className="p-2 hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="Zoom out"
                title="Zoom out (10% steps)"
              >
                <ZoomOut className="w-3.5 h-3.5" />
              </button>
              <button
                data-testid="zoom-reset-btn"
                onClick={zoomReset}
                className="mono text-xs px-2 py-2 tabular-nums whitespace-nowrap hover:bg-secondary transition"
                aria-label="Reset zoom"
                title="Reset zoom (100%)"
              >
                {Math.round(zoom * 100)}%
              </button>
              <button
                data-testid="zoom-in-btn"
                onClick={zoomIn}
                disabled={zoom >= ZOOM_MAX - 1e-3}
                className="p-2 hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="Zoom in"
                title="Zoom in (10% steps)"
              >
                <ZoomIn className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          {(isPdf(doc) || isPptx(doc)) && (
            <div
              className="flex items-center gap-2 border border-border rounded-md"
              data-testid="pdf-pager"
              title="Jump between pages — the document also scrolls naturally"
            >
              <button
                data-testid="pdf-prev-page"
                onClick={() => {
                  const next = Math.max(1, page - 1);
                  setPage(next);
                  pdfRef.current?.scrollToPage?.(next);
                }}
                disabled={page <= 1}
                className="p-2 hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="Previous page"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              <span className="mono text-xs px-2 tabular-nums whitespace-nowrap">
                {page} / {totalPages}
              </span>
              <button
                data-testid="pdf-next-page"
                onClick={() => {
                  const next = Math.min(totalPages, page + 1);
                  setPage(next);
                  pdfRef.current?.scrollToPage?.(next);
                }}
                disabled={page >= totalPages}
                className="p-2 hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="Next page"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          {isDocx(doc) && (
            <div
              className="flex items-center gap-2 border border-border rounded-md"
              data-testid="docx-pager"
              title="Page-up / page-down — the document still scrolls freely"
            >
              <button
                data-testid="docx-page-up"
                onClick={() => scrollDocxBy(docxRef.current, -0.85)}
                className="p-2 hover:bg-secondary"
                aria-label="Page up"
              >
                <ChevronUp className="w-3.5 h-3.5" />
              </button>
              <span className="mono text-[10px] uppercase tracking-wider px-2 whitespace-nowrap text-muted-foreground">
                Page&nbsp;up&nbsp;/&nbsp;down
              </span>
              <button
                data-testid="docx-page-down"
                onClick={() => scrollDocxBy(docxRef.current, 0.85)}
                className="p-2 hover:bg-secondary"
                aria-label="Page down"
              >
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          <button
            data-testid="doc-note-btn"
            onClick={() => setNoteOpen(true)}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-border hover:bg-secondary transition"
            title="Leave a note on this page"
          >
            <StickyNote className="w-3.5 h-3.5 text-amber-500" />
            <span className="hidden sm:inline">Note this page</span>
          </button>
        </div>
      </div>

      {/* Floating close button — positioned well below the sticky toolbar
          so it sits over the document body, never overlapping page-nav or
          the note button. */}
      <button
        data-testid="doc-close-btn"
        onClick={closeDoc}
        aria-label="Close document"
        title="Close document (return to folder)"
        className="fixed right-6 top-36 z-30 w-11 h-11 rounded-full bg-card border border-border shadow-lg flex items-center justify-center text-muted-foreground hover:text-destructive-foreground hover:bg-destructive hover:border-destructive transition"
      >
        <X className="w-5 h-5" />
      </button>

      <div className="p-6 pt-4">

      <div className="border border-border bg-card relative overflow-hidden isolate" style={{ minHeight: "70vh" }}>
        <div className="relative z-10 p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-display text-xl tracking-tight">{doc.name}</h2>
            <span className="mono text-xs text-muted-foreground">{doc.mime} · {(doc.size / 1024).toFixed(1)} KB</span>
          </div>

          {err && <div className="border border-destructive/30 bg-destructive/10 text-destructive p-4 rounded-md">{err}</div>}

          {/* Sheet tabs are hoisted OUT of the .document-surface canvas so
              they follow the app's actual theme (dark or light) instead
              of inheriting the forced-white surface colors that made them
              illegible in dark mode. They sit directly above the
              spreadsheet so the visual association is preserved. */}
          {!err && isXlsx(doc) && sheets.length > 1 && (
            <div className="flex gap-1 border-b border-border flex-wrap" data-testid="xlsx-sheet-tabs">
              {sheets.map((s, i) => (
                <button
                  key={`${s.name}:${i}`}
                  data-testid={`xlsx-sheet-tab-${i}`}
                  onClick={() => setSheetTab(i)}
                  className={`px-3 py-1.5 text-sm rounded-t-md font-medium border-b-2 transition ${
                    sheetTab === i
                      ? "border-primary bg-primary/10 text-foreground"
                      : "border-transparent text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                  }`}
                >{s.name}</button>
              ))}
            </div>
          )}

          {/* Document body — always rendered on a light surface so contents stay
              readable regardless of the app theme (dark mode would otherwise
              bleed through transparent PDF/image backgrounds). Right-click is
              suppressed so users cannot reach Save / Print via the context menu.
              The fence watermark is mounted INSIDE this surface (not the outer
              card) so it overlays only the document body and never leaks over
              the sticky toolbar, the document title row, or the page chrome. */}
          <div
            className="document-surface relative overflow-hidden bg-white text-black rounded-md"
            style={{ colorScheme: "light" }}
            onContextMenu={(e) => e.preventDefault()}
            onDragStart={(e) => e.preventDefault()}
          >
            <FenceWatermark
              user={user}
              extra={`${room?.name || ""} · p.${page}`}
              // Industry standard: forensic watermark visible to every
              // non-privileged viewer (fence-view). Admins and App Owners
              // see clean source so they can validate the original content.
              enabled={!isAdmin}
            />

          {!err && (isPdf(doc) || isPptx(doc)) && pdfData && (
            <PdfCanvasViewer
              ref={pdfRef}
              data={pdfData}
              zoom={zoom}
              onLoaded={({ totalPages: tp }) => setPdfPages(tp)}
              onPageChange={(n) => setPage((p) => (n !== p ? n : p))}
              onError={(e) => setErr(e?.message || "Could not render PDF.")}
            />
          )}

          {!err && isImage(doc) && imageBlobUrl && (
            <div className="bg-white p-4 flex items-center justify-center overflow-auto" style={{ maxHeight: "78vh" }}>
              <img
                src={imageBlobUrl}
                alt={doc.name}
                className="no-select"
                style={{ transform: `scale(${zoom})`, transformOrigin: "top center", maxWidth: "100%" }}
                draggable={false}
              />
            </div>
          )}

          {!err && isXlsx(doc) && (
            <div>
              {sheets.length === 0 || !sheets[sheetTab]?.html ? (
                <div className="bg-white text-black p-8 rounded-md text-sm text-neutral-500 text-center xlsx-render">
                  This workbook is empty or has no visible cells. Download the original file to inspect it.
                </div>
              ) : (
                <div
                  className="bg-white text-black p-4 rounded-md overflow-auto xlsx-render"
                  style={{ maxHeight: "78vh" }}
                >
                  <div
                    style={{
                      // CSS-only zoom for spreadsheet HTML. `top left`
                      // origin keeps row 1 / column A anchored so the
                      // scrollbars stay meaningful as the sheet grows or
                      // shrinks. We also shift width inversely so the
                      // scroll container correctly reports overflow at
                      // every zoom level.
                      transform: `scale(${zoom})`,
                      transformOrigin: "top left",
                      width: `${100 / zoom}%`,
                    }}
                    dangerouslySetInnerHTML={{ __html: sanitize(sheets[sheetTab].html) }}
                  />
                </div>
              )}
            </div>
          )}

          {!err && isDocx(doc) && (
            renderedHtml ? (
              <div
                ref={docxRef}
                className="docx-render bg-white text-black mx-auto p-12 rounded-md"
                style={{
                  maxWidth: "850px",
                  minHeight: "70vh",
                  // CSS-only zoom for HTML — preserves crisp text without
                  // needing a re-render pass. `transform-origin: top center`
                  // keeps the reading anchor at the page top while the
                  // surface grows/shrinks symmetrically left ↔ right.
                  transform: `scale(${zoom})`,
                  transformOrigin: "top center",
                }}
                dangerouslySetInnerHTML={{ __html: sanitize(renderedHtml) }}
              />
            ) : (
              <div ref={docxRef} className="docx-render bg-white text-black mx-auto p-12 rounded-md text-sm text-neutral-500 text-center" style={{ maxWidth: "850px" }}>
                This document is empty or could not be rendered in-browser. Download the original to view it.
              </div>
            )
          )}

          {!err && isText(doc) && (
            <pre className="bg-white text-black p-6 rounded-md whitespace-pre-wrap font-mono text-sm leading-relaxed" style={{ maxHeight: "78vh", overflow: "auto" }}>
{textContent}
            </pre>
          )}

          {!err && !isPdf(doc) && !isImage(doc) && !isDocx(doc) && !isXlsx(doc) && !isText(doc) && (
            <div className="bg-white text-black border border-dashed border-neutral-300 p-12 text-center rounded-md">
              <p className="text-sm text-neutral-600">In-app preview not available for this file type.</p>
              <p className="mono text-xs mt-2 text-neutral-500">{doc.mime}</p>
              <p className="text-xs text-neutral-500 mt-3">Use the download buttons in the room view to grab the file.</p>
            </div>
          )}
          </div>
        </div>
      </div>
      {room?.watermark_text && (
        <div className="mt-3 label-mono text-muted-foreground text-center">— {room.watermark_text} —</div>
      )}
      </div>

      {noteOpen && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-6" onClick={() => setNoteOpen(false)}>
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={async (e) => {
              e.preventDefault();
              if (!noteText.trim() || noteBusy) return;
              setNoteBusy(true);
              try {
                await api.post(`/rooms/${roomId}/questions`, {
                  text: noteText.trim(),
                  doc_id: docId,
                  page: isPdf(doc) ? page : undefined,
                });
                toast.success(`Note posted${isPdf(doc) ? ` for page ${page}` : ""} — admins will see it in Q&A.`);
                setNoteText("");
                setNoteOpen(false);
              } catch (e2) {
                toast.error(e2?.response?.data?.detail || "Could not post note.");
              } finally {
                setNoteBusy(false);
              }
            }}
            className="bg-card border border-border rounded-md w-full max-w-md p-6 relative"
            data-testid="doc-note-dialog"
          >
            <button
              type="button" onClick={() => setNoteOpen(false)} aria-label="Close"
              className="absolute top-3 right-3 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-start gap-3 pr-6">
              <div className="w-10 h-10 rounded-md bg-amber-500/15 flex items-center justify-center flex-shrink-0">
                <StickyNote className="w-5 h-5 text-amber-600" />
              </div>
              <div className="min-w-0">
                <h3 className="font-display text-lg tracking-tight">Note on this {isPdf(doc) ? `page (${page})` : "document"}</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Posts a question/comment anchored to {isPdf(doc) ? `page ${page} of ` : ""}<span className="font-mono">{doc.name}</span>.
                  Admins will see it in the room&apos;s Q&amp;A tab.
                </p>
              </div>
            </div>
            <textarea
              data-testid="doc-note-text"
              required value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              rows={4} autoFocus
              placeholder="What would you like to flag on this page?"
              className="mt-4 w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            />
            <div className="mt-4 flex gap-2 justify-end">
              <button
                type="button" onClick={() => setNoteOpen(false)}
                className="px-4 py-2 text-sm rounded-md border border-border hover:bg-secondary transition"
              >
                Cancel
              </button>
              <button
                data-testid="doc-note-submit"
                type="submit" disabled={noteBusy}
                className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition disabled:opacity-50"
              >
                {noteBusy ? "Posting…" : "Post note"}
              </button>
            </div>
          </form>
        </div>
      )}
      {requestOpen && (
        <RequestAccessModal
          roomId={roomId}
          docId={docId}
          docName={doc?.name}
          isFolder={doc?.type === "folder"}
          onClose={() => setRequestOpen(false)}
          onDone={() => setRequestOpen(false)}
        />
      )}
      {/* Persistent Ada bubble — same one you had open in the room */}
      <AdaBubble roomId={roomId} room={room} isAdmin={viewerIsAdmin} />
    </div>
  );
}

